import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
  FormControlLabel,
  Divider,
  Radio,
  RadioGroup,
  useMediaQuery,
  useTheme,
  Popover,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Upload_Icon from "../../images/vehicle/Upload_icon.svg";
import Upload_Edit_Icon from "../../images/vehicle/Upload_Edit_icon.svg";
import { isRouteEyeAdmin } from "../../utils/isRouteEyeAdmin";
import { ChromePicker } from "react-color";
import { getAllTrackingDevices } from "../../components/device/deviceApi";
import { ReactComponent as ArrowDownIcon } from "../../images/table/Arrow_down.svg";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  getUserDateFormat,
  convertToDateFnsFormat,
} from "../../utils/formatDate";

// Validation utility functions
const validateRequired = (value, fieldName) => {
  if (!value || (typeof value === "string" && value.trim() === "")) {
    return `${fieldName} is required`;
  }
  return "";
};
// Mechanic and Mechanic Phone Number validation
const validateMechanic = (value) => {
  if (!value) return "";
  if (value.length < 2) {
    return "Mechanic name must be at least 2 characters";
  }
  if (value.length > 100) {
    return "Mechanic name cannot exceed 100 characters";
  }
  return "";
};

const validateMechanicPhoneNumber = (value) => {
  if (!value) return "";
  // Accepts numbers, spaces, +, -, ()
  const regex = /^[0-9+\-()\s]{10,20}$/;
  if (!regex.test(value)) {
    return "Invalid phone number format";
  }
  return "";
};

const ORGANIZATION_OWNED = "ORGANIZATION";
const CONTRACTOR_OWNED = "CONTRACTOR";

const EMPTY_CONTRACTOR_DETAILS = {
  contractorName: "",
  contractorPhone: "",
  contractorEmail: "",
};

const getVehicleOwnershipType = (value) => {
  const raw = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[\s-]+/g, "_");
  if (raw === CONTRACTOR_OWNED || raw === "THIRD_PARTY" || raw === "THIRDPARTY") {
    return CONTRACTOR_OWNED;
  }
  return ORGANIZATION_OWNED;
};

const validateContractorEmail = (value) => {
  if (!value) return "";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
    return "Invalid email format";
  }
  return "";
};
const validateAlphaNumeric = (value, fieldName) => {
  if (!value) return "";
  const regex = /^[a-zA-Z0-9\s-]*$/;
  if (!regex.test(value)) {
    return `${fieldName} must contain only letters, numbers, spaces and hyphens`;
  }
  return "";
};

const validateYear = (value) => {
  if (!value) return "";
  const year = parseInt(value, 10);
  const currentYear = new Date().getFullYear();

  if (isNaN(year) || value.length !== 4) {
    return "Please enter a valid 4-digit year";
  }

  if (year < 1900 || year > currentYear) {
    return `Year must be between 1900 and ${currentYear}`;
  }
  return "";
};

const validateSeatCapacity = (value) => {
  if (!value) return "";
  const capacity = Number(value);

  if (!Number.isInteger(capacity) || capacity <= 0) {
    return "Seat capacity must be a positive number";
  }

  return "";
};

const validateChassisNumber = (value) => {
  if (!value) return "";
  const regex = /^[A-HJ-NPR-Za-hj-npr-z0-9]{17}$/i;

  if (value.length !== 17) {
    return "Chassis number must be exactly 17 characters";
  }

  if (!regex.test(value)) {
    return "Invalid chassis number format";
  }
  return "";
};

const validateInsurancePolicy = (value) => {
  if (!value) return "";
  const regex = /^[A-Za-z0-9-/]*$/;

  if (!regex.test(value)) {
    return "Policy number can only contain letters, numbers, hyphens and slashes";
  }
  return "";
};

const validateDate = (value, fieldName) => {
  if (!value) return "";

  const selectedDate = new Date(value);

  if (isNaN(selectedDate.getTime())) {
    return `Please enter a valid date for ${fieldName}`;
  }
  return "";
};

const validatePastOrTodayDate = (value, fieldName) => {
  if (!value) return "";

  const selectedDate = new Date(value);

  if (isNaN(selectedDate.getTime())) {
    return `Please enter a valid date for ${fieldName}`;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  selectedDate.setHours(0, 0, 0, 0);

  if (selectedDate > today) {
    return `${fieldName} cannot be a future date`;
  }

  return "";
};

const validateDescription = (value) => {
  if (!value) return "";

  if (value.length < 2) {
    return "Description must be at least 2 characters";
  }

  if (value.length > 500) {
    return "Description cannot exceed 500 characters";
  }
  return "";
};

const VEHICLE_TYPE_OPTIONS = [
  { value: "BUS", label: "Bus" },
  { value: "VAN", label: "Van" },
  { value: "CAR", label: "Car" },
  { value: "TRUCK", label: "Truck" },
  { value: "BIKE", label: "Bike" },
  { value: "OTHER", label: "Other" },
];

const BODY_TYPE_OPTIONS = [
  { value: "SUV", label: "SUV" },
  { value: "SEDAN", label: "Sedan" },
  { value: "HATCHBACK", label: "Hatchback" },
  { value: "TRUCK", label: "Truck" },
  { value: "VAN", label: "Van" },
  { value: "COUPE", label: "Coupe" },
  { value: "CONVERTIBLE", label: "Convertible" },
  { value: "OTHER", label: "Other" },
];

const FUEL_TYPE_OPTIONS = [
  { value: "PETROL", label: "Petrol" },
  { value: "DIESEL", label: "Diesel" },
  { value: "ELECTRIC", label: "Electric" },
  { value: "OTHER", label: "Other" },
];

const AIR_CONDITION_OPTIONS = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

const getOptionLabel = (options, value) => {
  return options.find((option) => option.value === value)?.label || value;
};

// VehicleAddDetails Component
export function VehicleAddDetails({
  vehicleFormData,
  setVehicleFormData,
  selectedImage,
  setSelectedImage,
  routes = [],
  drivers = [],
  sites = [],
  attendants = [],
  errors = {},
  setErrors,
  isEditing,
  selectedSchedules,
  setSelectedSchedules,
  onValidationChange,
  setColor,
  color,
  embedded = false,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [userFeatures, setUserFeatures] = useState([]);
  const [touched, setTouched] = useState({});
  const [existingVehicleIds, setExistingVehicleIds] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [deviceData, setDeviceData] = useState([]);

  console.log(deviceData);
  const theme = useTheme();
  const userDateFormat = getUserDateFormat();
  const todayForDateInput = new Date().toISOString().split("T")[0];

  const getDatePlaceholder = () => {
    return userDateFormat;
  };

  const getDateInputFormat = () => {
    return convertToDateFnsFormat(userDateFormat);
  };
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isExtraSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

  const handleColorPickerOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleColorPickerClose = () => {
    setAnchorEl(null);
  };
  const [initialVehicleNumber, setInitialVehicleNumber] = useState("");

  const hasPermission = (apiPath, action) => {
    return userFeatures.some(
      (feature) => feature.api_path === apiPath && feature.action === action,
    );
  };

  // useEffect(() => {
  //   const devices = getAllTrackingDevices()
  //   setDeviceData(devices)
  //   console.log(devices)

  //   if (isEditing && vehicleFormData.VehicleNumber && !initialVehicleNumber) {
  //     setInitialVehicleNumber(vehicleFormData.VehicleNumber);
  //   }
  // }, [isEditing, vehicleFormData.VehicleNumber, initialVehicleNumber]);

  useEffect(() => {
    const fetchDevices = async () => {
      try {
        const devices = await getAllTrackingDevices();
        // Ensure we always set an array
        setDeviceData(Array.isArray(devices.data) ? devices.data : []);
        console.log(devices?.data);
      } catch (error) {
        console.error("Error fetching devices:", error);
        setDeviceData([]); // Always set empty array on error
      }
    };

    fetchDevices();

    if (isEditing && vehicleFormData.VehicleNumber && !initialVehicleNumber) {
      setInitialVehicleNumber(vehicleFormData.VehicleNumber);
    }
  }, [isEditing, vehicleFormData.VehicleNumber, initialVehicleNumber]);

  useEffect(() => {
    try {
      const localDataString = localStorage.getItem("userState");
      if (localDataString) {
        const localData = JSON.parse(localDataString);
        if (localData && localData.features) {
          setUserFeatures(localData.features);
        }
      }
    } catch (error) {
      console.error("Error loading features from localStorage:", error);
    }
  }, []);

  useEffect(() => {
    // Set imagePreview for existing images when in edit mode
    if (
      isEditing &&
      selectedImage &&
      selectedImage.isExisting &&
      selectedImage.location
    ) {
      setVehicleFormData((prev) => ({
        ...prev,
        imagePreview: selectedImage.location,
      }));
    }
  }, [isEditing, selectedImage]);

  useEffect(() => {
    const isValid = validateForm();
    onValidationChange(isValid, errors);
    // console.log("VehicleAddDetails Validation:", {
    //   isValid,
    //   errors,
    //   vehicleFormData,
    // });

    // Mark fields as touched if they have errors from parent
    if (errors.name || errors.VehicleNumber) {
      setTouched((prev) => ({
        ...prev,
        name: !!errors.name,
        VehicleNumber: !!errors.VehicleNumber,
      }));
    }
  }, [vehicleFormData, touched, errors, onValidationChange]);

  const validateForm = () => {
    const newErrors = {};

    newErrors.name = validateRequired(
      vehicleFormData.name,
      t("Vehicle Name"),
      t,
    );
    newErrors.VehicleNumber = validateRequired(
      vehicleFormData.VehicleNumber,
      t("vehicleNumber"),
      t,
    );
    // newErrors.description = validateDescription(vehicleFormData.description, t);

    // Description is now not mandatory, but still validate if provided
    if (vehicleFormData.description) {
      newErrors.description = validateDescription(
        vehicleFormData.description,
        t,
      );
    }

    // Non-mandatory fields (validate only if filled)
    if (vehicleFormData.Make) {
      newErrors.Make = validateAlphaNumeric(vehicleFormData.Make, t("make"), t);
    }
    if (vehicleFormData.Model) {
      newErrors.Model = validateAlphaNumeric(
        vehicleFormData.Model,
        t("model"),
        t,
      );
    }
    if (vehicleFormData.Year) {
      newErrors.Year = validateYear(vehicleFormData.Year, t);
    }
    if (vehicleFormData.seatCapacity) {
      newErrors.seatCapacity = validateSeatCapacity(
        vehicleFormData.seatCapacity,
      );
    }
    if (vehicleFormData.chassisNumber) {
      newErrors.chassisNumber = validateChassisNumber(
        vehicleFormData.chassisNumber,
        t,
      );
    }
    if (vehicleFormData.insurancePolicyNumber) {
      newErrors.insurancePolicyNumber = validateInsurancePolicy(
        vehicleFormData.insurancePolicyNumber,
        t,
      );
    }
    if (vehicleFormData.insurancePolicyExpiry) {
      newErrors.insurancePolicyExpiry = validateDate(
        vehicleFormData.insurancePolicyExpiry,
        t("insurancePolicyExpiryDate"),
        t,
      );
    }
    if (vehicleFormData.serviceDueDate) {
      newErrors.serviceDueDate = validateDate(
        vehicleFormData.serviceDueDate,
        t("serviceDueDate"),
        t,
      );
    }
    if (vehicleFormData.lastServiceDate) {
      newErrors.lastServiceDate = validatePastOrTodayDate(
        vehicleFormData.lastServiceDate,
        t("Last Service Date"),
      );
    }
    if (vehicleFormData.reregistrationDueDate) {
      newErrors.reregistrationDueDate = validateDate(
        vehicleFormData.reregistrationDueDate,
        t("re-registrationDueDate"),
        t,
      );
    }
    if (vehicleFormData.lastEmissionTestDate) {
      newErrors.lastEmissionTestDate = validatePastOrTodayDate(
        vehicleFormData.lastEmissionTestDate,
        t("Last Emission Test Date"),
      );
    }

    // Mechanic and Mechanic Phone Number validation (non-mandatory, but validate if provided)
    if (vehicleFormData.mechanic) {
      newErrors.mechanic = validateMechanic(vehicleFormData.mechanic);
    }
    if (vehicleFormData.mechanicPhoneNumber) {
      newErrors.mechanicPhoneNumber = validateMechanicPhoneNumber(
        vehicleFormData.mechanicPhoneNumber,
      );
    }

    // Contractor details are optional, but validate the format when filled in
    if (getVehicleOwnershipType(vehicleFormData.ownershipType) === CONTRACTOR_OWNED) {
      newErrors.contractorPhone = validateMechanicPhoneNumber(
        vehicleFormData.contractorPhone,
      );
      newErrors.contractorEmail = validateContractorEmail(
        vehicleFormData.contractorEmail,
      );
    }

    // Dependency: Insurance policy expiry required if number is provided
    if (
      vehicleFormData.insurancePolicyNumber &&
      !vehicleFormData.insurancePolicyExpiry
    ) {
      newErrors.insurancePolicyExpiry =
        "Insurance policy expiry date is required when policy number is provided";
    }

    setErrors(newErrors);
    const isValid = !Object.values(newErrors).some((error) => error !== "");
    console.log(isValid, "isValid");
    return isValid;
  };

  const handleInputChange = (e, fieldName) => {
    const value = e.target.value;
    if (fieldName === "ownershipType") {
      setVehicleFormData((prev) => ({
        ...prev,
        ownershipType: getVehicleOwnershipType(value),
        ...(getVehicleOwnershipType(value) === ORGANIZATION_OWNED
          ? EMPTY_CONTRACTOR_DETAILS
          : {}),
      }));
      setTouched((prev) => ({
        ...prev,
        ownershipType: true,
      }));
      setErrors((prev) => ({
        ...prev,
        contractorName: "",
        contractorPhone: "",
        contractorEmail: "",
      }));
      return;
    }

    setVehicleFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    let errorMessage = "";
    switch (fieldName) {
      case "name":
      case "VehicleNumber":
        errorMessage = validateRequired(value, t(fieldName), t);
        break;
      case "Make":
      case "Model":
        errorMessage = validateAlphaNumeric(value, t(fieldName), t);
        break;
      case "mechanic":
        errorMessage = validateMechanic(value);
        break;
      case "mechanicPhoneNumber":
      case "contractorPhone":
        errorMessage = validateMechanicPhoneNumber(value);
        break;
      case "contractorEmail":
        errorMessage = validateContractorEmail(value);
        break;
      case "Year":
        errorMessage = validateYear(value, t);
        break;
      case "seatCapacity":
        errorMessage = validateSeatCapacity(value);
        break;
      case "chassisNumber":
        errorMessage = validateChassisNumber(value, t);
        break;
      case "insurancePolicyNumber":
        errorMessage = validateInsurancePolicy(value, t);
        break;
      case "insurancePolicyExpiry":
      case "serviceDueDate":
      case "reregistrationDueDate":
        errorMessage = validateDate(value, t(fieldName), t);
        break;
      case "lastServiceDate":
        errorMessage = validatePastOrTodayDate(value, t("Last Service Date"));
        break;
      case "lastEmissionTestDate":
        errorMessage = validatePastOrTodayDate(
          value,
          t("Last Emission Test Date"),
        );
        break;
      default:
        break;
    }

    setErrors((prev) => ({
      ...prev,
      [fieldName]: errorMessage,
    }));

    // Re-validate dependent fields
    if (fieldName === "insurancePolicyNumber" && value) {
      const expiryError = vehicleFormData.insurancePolicyExpiry
        ? validateDate(
            vehicleFormData.insurancePolicyExpiry,
            t("insurancePolicyExpiryDate"),
            t,
          )
        : t("validation.requiredWithField", {
            field: t("insurancePolicyExpiryDate"),
            dependentField: t("insurancePolicyNumber"),
          });

      setErrors((prev) => ({
        ...prev,
        insurancePolicyExpiry: expiryError,
      }));
    }
  };

  const handleBlur = (fieldName) => {
    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));
    validateForm();
  };

  const handleDropdownChange = (e, fieldName) => {
    const value = e.target.value;

    switch (fieldName) {
      case "route":
        const selectedRoute = routes.find(
          (route) =>
            String(route.id) === String(value) ||
            String(route.value) === String(value),
        );
        setVehicleFormData((prev) => ({
          ...prev,
          routes: selectedRoute ? selectedRoute.id : "", // Use 'routes' to match VehicleAddMain
        }));
        break;
      case "driver":
        const selectedDriver = drivers.find(
          (driver) => String(driver.id) === String(value),
        );
        setVehicleFormData((prev) => ({
          ...prev,
          driverId: selectedDriver ? selectedDriver.id : "", // Use 'driverId' to match VehicleAddMain
        }));
        break;
      case "attendant":
        const selectedAttendant = attendants.find(
          (attendant) => String(attendant.id) === String(value),
        );
        setVehicleFormData((prev) => ({
          ...prev,
          attendantId: selectedAttendant ? selectedAttendant.id : "",
        }));
        break;
      case "site":
        setVehicleFormData((prev) => ({
          ...prev,
          sites: value,
          routes: String(prev.sites || "") === String(value || "") ? prev.routes : "",
        }));
        break;
      default:
        setVehicleFormData((prev) => ({
          ...prev,
          [fieldName]: value,
        }));
    }

    setTouched((prev) => ({
      ...prev,
      [fieldName]: true,
    }));

    validateForm();
  };

  const handleImageUpload = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 10 * 1024 * 1024) {
        setErrors((prev) => ({
          ...prev,
          image: t("validation.fileSizeExceeded", { max: "10MB" }),
        }));
        return;
      }

      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        setErrors((prev) => ({
          ...prev,
          image: t("validation.invalidFileType", { types: "PNG, JPG, GIF" }),
        }));
        return;
      }

      setErrors((prev) => ({
        ...prev,
        image: "",
      }));

      setSelectedImage(file); // Set as File object for new uploads
      const imageUrl = URL.createObjectURL(file);
      setVehicleFormData((prev) => ({
        ...prev,
        imagePreview: imageUrl,
      }));
    }
  };

  const handleRemoveImage = () => {
    if (selectedImage instanceof File && vehicleFormData.imagePreview) {
      URL.revokeObjectURL(vehicleFormData.imagePreview);
    }
    setSelectedImage(null);
    setVehicleFormData((prev) => ({
      ...prev,
      imagePreview: null,
    }));
  };

  const formatDateForInput = (isoDate) => {
    if (!isoDate) return "";
    try {
      const date = new Date(isoDate);
      if (isNaN(date.getTime())) return "";
      return date.toISOString().split("T")[0];
    } catch (error) {
      console.error("Error formatting date:", error);
      return "";
    }
  };

  const handleOpenSchedule = () => {
    if (vehicleFormData.id) {
      navigate(`/vehicle/schedule?vehicleId=${vehicleFormData.id}`);
    }
  };

  const inputStyles = {
    height: 55,
    fontWeight: "medium",
    "& .MuiOutlinedInput-root": {
      height: 55,
      borderRadius: "16px",
      "& fieldset": {
        border: "none",
      },
      "&:hover fieldset": {
        border: "none",
      },
      "&.Mui-focused fieldset": {
        border: "none",
      },
      border: (theme) => `2px solid ${theme.palette.grey[800]}`,
      "&.Mui-error": {
        border: "2px solid #d32f2f",
      },
    },
    "& .MuiInputBase-input": {
      padding: "16px 24px",
    },
    "& .MuiFormHelperText-root": {
      marginLeft: "14px",
      marginTop: "3px",
      color: "#d32f2f",
    },
  };

  const selectStyles = {
    "& .MuiSelect-select": {
      borderRadius: "16px",
      border: (theme) => `2px solid ${theme.palette.grey[800]}`,
      padding: "16px 24px",
    },
    "& .MuiSelect-select.MuiSelect-outlined:focus": {
      borderRadius: "16px",
      backgroundColor: "transparent",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
    "&.Mui-error .MuiSelect-select": {
      border: "2px solid #d32f2f",
    },
  };

  const RequiredLabel = ({ label }) => (
    <Box sx={{ mb: 1, display: "flex", alignItems: "center" }}>
      <Typography variant="body2" component="span">
        {label}
      </Typography>
      <Typography
        variant="body2"
        component="span"
        color="error"
        sx={{ ml: 0.5 }}
      >
        *
      </Typography>
    </Box>
  );

  const StandardLabel = ({ label }) => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
  console.log(vehicleFormData?.name);
  return (
    <Box
      sx={{
        padding: { xs: "20px", md: "40px" },
        margin: embedded
          ? 0
          : { xs: "0px 20px", md: "0px 40px 0px 40px" },
        backgroundColor: "#FFFFFF",
        border: "1px solid #A5DCF3",
        borderRadius: "10px",
        height: "100%",
        boxShadow: "-2px 2px 5px 0px #00000040",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Paper
          sx={{
            height: 52,
            width: isEditing ? "calc(66.67% - 15px)" : "66.77%",
            marginRight: "20px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontFamily: "Roboto",
              fontSize: isSmallScreen ? "20px" : "24px",
              lineHeight: "100%",
              color: "#FFFFFF",
              marginBottom: isExtraSmallScreen ? "10px" : 0,
            }}
          >
            {t("vehicleDetails")}
            {/* {isEditing && initialVehicleNumber && (
              <>
                : {initialVehicleNumber}
              </>
            )} */}
          </Typography>
        </Paper>

        {isEditing ? (
          hasPermission("/api/schedule/vehicle", "PUT") && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleOpenSchedule}
              sx={{
                // width: 224,
                height: 47,
                borderRadius: "8px",
                gap: "10px",
                padding: "12px 24px",
                background: "#20A8E0",
                fontFamily: "Roboto",
                fontWeight: 600,
                textTransform: "none",
                color: "#FFFFFF",
                fontSize: "16px",
                "&:hover": {
                  background: "#117aa7ff",
                },
              }}
            >
              {t("View / Assign Schedules")}
            </Button>
          )
        ) : (
          <></>
          // <Button
          //   variant="contained"
          //   disabled
          //   sx={{
          //     width: 224,
          //     height: 47,
          //     borderRadius: "8px",
          //     gap: "10px",
          //     padding: "12px 24px",
          //     background: "#F2F2F3",
          //     textTransform: "none",
          //     color: "black !important",
          //     border: "2px solid black",
          //     cursor: "not-allowed",
          //   }}
          // >
          //   {t("assignSchedules")}
          // </Button>
        )}
      </Box>
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {/* Vehicle ownership */}
              <Grid container rowSpacing={3} columnSpacing={2}>
                <Grid item xs={12}>
                  <StandardLabel label={t("Vehicle Ownership")} />
                  <RadioGroup
                    row
                    value={getVehicleOwnershipType(
                      vehicleFormData.ownershipType,
                    )}
                    onChange={(e) => handleInputChange(e, "ownershipType")}
                  >
                    <FormControlLabel
                      value={ORGANIZATION_OWNED}
                      control={<Radio sx={{ color: "#20A8E0" }} />}
                      label={t("Organization owned")}
                    />
                    <FormControlLabel
                      value={CONTRACTOR_OWNED}
                      control={<Radio sx={{ color: "#20A8E0" }} />}
                      label={t("Contractor / third party")}
                    />
                  </RadioGroup>
                </Grid>
                {getVehicleOwnershipType(vehicleFormData.ownershipType) ===
                  CONTRACTOR_OWNED && (
                  <>
                    <Grid item xs={12} sm={4}>
                      <StandardLabel label={t("Contractor Name")} />
                      <TextField
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        placeholder={t("Type contractor / company name")}
                        value={vehicleFormData.contractorName || ""}
                        onChange={(e) => handleInputChange(e, "contractorName")}
                        onBlur={() => handleBlur("contractorName")}
                        error={
                          !!(touched.contractorName && errors.contractorName)
                        }
                        helperText={
                          touched.contractorName && errors.contractorName
                            ? errors.contractorName
                            : ""
                        }
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StandardLabel label={t("Contractor Phone")} />
                      <TextField
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        placeholder={t("Type contractor phone number")}
                        value={vehicleFormData.contractorPhone || ""}
                        onChange={(e) => handleInputChange(e, "contractorPhone")}
                        onBlur={() => handleBlur("contractorPhone")}
                        error={
                          !!(touched.contractorPhone && errors.contractorPhone)
                        }
                        helperText={
                          touched.contractorPhone && errors.contractorPhone
                            ? errors.contractorPhone
                            : ""
                        }
                        sx={inputStyles}
                      />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <StandardLabel label={t("Contractor Email")} />
                      <TextField
                        fullWidth
                        variant="outlined"
                        InputLabelProps={{ shrink: false }}
                        placeholder={t("Type contractor email")}
                        value={vehicleFormData.contractorEmail || ""}
                        onChange={(e) => handleInputChange(e, "contractorEmail")}
                        onBlur={() => handleBlur("contractorEmail")}
                        error={
                          !!(touched.contractorEmail && errors.contractorEmail)
                        }
                        helperText={
                          touched.contractorEmail && errors.contractorEmail
                            ? errors.contractorEmail
                            : ""
                        }
                        sx={inputStyles}
                      />
                    </Grid>
                  </>
                )}
              </Grid>

              {/* Negative margin offsets the parent's gap so the rule sits
                  centred in a single 24px row gap instead of two. */}
              <Divider sx={{ my: -1.5, borderColor: "#A5DCF3" }} />

              {/* First row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <RequiredLabel label={t("Vehicle Name")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={`${t("Type Vehicle Name")} (e.g.Shuttle Van)`}
                    value={vehicleFormData.name || ""}
                    onChange={(e) => handleInputChange(e, "name")}
                    onBlur={() => handleBlur("name")}
                    error={!!(touched.name && errors.name)}
                    helperText={touched.name && errors.name ? errors.name : ""}
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <RequiredLabel label={t("vehicleNumber")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={`${t("Type Vehicle Number")} (e.g.KA01AB1234)`}
                    value={vehicleFormData.VehicleNumber || ""}
                    onChange={(e) => handleInputChange(e, "VehicleNumber")}
                    onBlur={() => handleBlur("VehicleNumber")}
                    error={!!(touched.VehicleNumber && errors.VehicleNumber)}
                    helperText={
                      touched.VehicleNumber && errors.VehicleNumber
                        ? errors.VehicleNumber
                        : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>

              {/* Second row */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StandardLabel label={t("make")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={`${t("Type Make Details")} (e.g.Suzuki Swift)`}
                    value={vehicleFormData.Make || ""}
                    onChange={(e) => handleInputChange(e, "Make")}
                    onBlur={() => handleBlur("Make")}
                    error={!!(touched.Make && errors.Make)}
                    helperText={touched.Make && errors.Make ? errors.Make : ""}
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StandardLabel label={t("model")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={`${t("Type Model")} (e.g. Swift VXI)`}
                    value={vehicleFormData.Model || ""}
                    onChange={(e) => handleInputChange(e, "Model")}
                    onBlur={() => handleBlur("Model")}
                    error={!!(touched.Model && errors.Model)}
                    helperText={
                      touched.Model && errors.Model ? errors.Model : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <StandardLabel label={t("chassisNumber")} />
                  <TextField
                    fullWidth
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder={`${t(
                      "Type Chassis Number",
                    )} (e.g.MA3EJKV1S00123456)`}
                    value={vehicleFormData.chassisNumber || ""}
                    onChange={(e) => handleInputChange(e, "chassisNumber")}
                    onBlur={() => handleBlur("chassisNumber")}
                    error={!!(touched.chassisNumber && errors.chassisNumber)}
                    helperText={
                      touched.chassisNumber && errors.chassisNumber
                        ? errors.chassisNumber
                        : ""
                    }
                    sx={inputStyles}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <StandardLabel label={t("yearofManufacture")} />
                  <TextField
                    fullWidth
                    type="text"
                    variant="outlined"
                    InputLabelProps={{ shrink: false }}
                    placeholder="YYYY"
                    value={vehicleFormData.Year || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^\d{0,4}$/.test(value)) {
                        handleInputChange(e, "Year");
                      }
                    }}
                    inputProps={{
                      maxLength: 4,
                      pattern: "[0-9]*",
                      inputMode: "numeric",
                    }}
                    onBlur={() => handleBlur("Year")}
                    error={!!(touched.Year && errors.Year)}
                    helperText={touched.Year && errors.Year ? errors.Year : ""}
                    sx={inputStyles}
                  />
                </Grid>
              </Grid>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <StandardLabel label={t("Vehicle Type")} />
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!(touched.vehicleType && errors.vehicleType)}
                  >
                    <Select
                      displayEmpty
                      value={vehicleFormData.vehicleType || ""}
                      onChange={(e) => handleDropdownChange(e, "vehicleType")}
                      onBlur={() => handleBlur("vehicleType")}
                      inputProps={{ "aria-label": "Vehicle Type" }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography color="text.secondary">
                              {t("Select Vehicle Type")}
                            </Typography>
                          );
                        }
                        return getOptionLabel(VEHICLE_TYPE_OPTIONS, selected);
                      }}
                      sx={selectStyles}
                    >
                      <MenuItem value="">
                        <em>{t("None")}</em>
                      </MenuItem>
                      {VEHICLE_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.vehicleType && errors.vehicleType && (
                      <FormHelperText error>
                        {errors.vehicleType}
                      </FormHelperText>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6} mb={1.5}>
                  <StandardLabel label={t("Body Type")} />
                  <FormControl
                    fullWidth
                    variant="outlined"
                    error={!!(touched.bodyType && errors.bodyType)}
                  >
                    <Select
                      displayEmpty
                      value={vehicleFormData.bodyType || ""}
                      onChange={(e) => handleDropdownChange(e, "bodyType")}
                      onBlur={() => handleBlur("bodyType")}
                      inputProps={{ "aria-label": "Body Type" }}
                      renderValue={(selected) => {
                        if (!selected) {
                          return (
                            <Typography color="text.secondary">
                              {t("Select Body Type")}
                            </Typography>
                          );
                        }
                        return getOptionLabel(BODY_TYPE_OPTIONS, selected);
                      }}
                      sx={selectStyles}
                    >
                      <MenuItem value="">
                        <em>{t("None")}</em>
                      </MenuItem>
                      {BODY_TYPE_OPTIONS.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {touched.bodyType && errors.bodyType && (
                      <FormHelperText error>{errors.bodyType}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                width: "100%",
                border: errors.image
                  ? "2px dashed #d32f2f"
                  : "2px dashed #183059",
                borderRadius: 2,
                height: 350,
                backgroundColor: "#E8EAEE",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              {vehicleFormData.imagePreview ? (
                <Box
                  sx={{ position: "relative", width: "100%", height: "100%" }}
                >
                  <img
                    src={vehicleFormData.imagePreview}
                    alt="Vehicle"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "contain",
                    }}
                  />
                  <IconButton
                    onClick={handleRemoveImage}
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      color: "white",
                    }}
                    size="small"
                  >
                    <img src={Upload_Edit_Icon} alt="Upload Edit" />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <img
                    src={Upload_Icon}
                    alt="Upload"
                    style={{ width: 50, height: 50 }}
                  />
                  <Typography
                    variant="body1"
                    sx={{
                      mt: 2,
                      color: "#1F242E",
                      fontFamily: "Roboto",
                      fontWeight: 600,
                      fontSize: "20px",
                      lineHeight: "100%",
                      letterSpacing: "0%",
                      textAlign: "center",
                    }}
                  >
                    {t("vehicleImage")}
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 2, color: "#666" }}>
                    {t("Click to upload or drag and drop")}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t("PNG, JPG, GIF up to 10MB")}
                  </Typography>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: "100%",
                      opacity: 0,
                      cursor: "pointer",
                    }}
                  />
                </>
              )}
            </Box>
            {errors.image && (
              <Typography variant="caption" color="error" sx={{ ml: 1.5 }}>
                {errors.image}
              </Typography>
            )}
          </Grid>
          {/* Full-width row: Fuel Type, Seat Capacity, Vehicle Color, Air Condition */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3} mb={1.5}>
                <StandardLabel label={t("Fuel Type")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.fuelType && errors.fuelType)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.fuelType || ""}
                    onChange={(e) => handleDropdownChange(e, "fuelType")}
                    onBlur={() => handleBlur("fuelType")}
                    inputProps={{ "aria-label": "Fuel Type" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Fuel Type")}
                          </Typography>
                        );
                      }
                      return getOptionLabel(FUEL_TYPE_OPTIONS, selected);
                    }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {FUEL_TYPE_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.fuelType && errors.fuelType && (
                    <FormHelperText error>{errors.fuelType}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} mb={1.5}>
                <StandardLabel label={t("Vehicle Seat Capacity")} />
                <TextField
                  fullWidth
                  type="text"
                  variant="outlined"
                  InputLabelProps={{ shrink: false }}
                  placeholder={`${t("Type Seat Capacity")} (e.g. 40)`}
                  value={vehicleFormData.seatCapacity || ""}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (/^\d*$/.test(value)) {
                      handleInputChange(e, "seatCapacity");
                    }
                  }}
                  inputProps={{
                    pattern: "[0-9]*",
                    inputMode: "numeric",
                  }}
                  onBlur={() => handleBlur("seatCapacity")}
                  error={!!(touched.seatCapacity && errors.seatCapacity)}
                  helperText={
                    touched.seatCapacity && errors.seatCapacity
                      ? errors.seatCapacity
                      : ""
                  }
                  sx={inputStyles}
                />
              </Grid>
              <Grid item xs={12} md={3} mb={1.5}>
                <StandardLabel label={t("Vehicle Color")} />
                <Box
                  onClick={handleColorPickerOpen}
                  sx={{
                    width: "100%",
                    height: 55,
                    borderRadius: "16px",
                    cursor: "pointer",
                    border: (theme) => `2px solid ${theme.palette.grey[800]}`,
                    padding: "16px 24px",
                    boxSizing: "border-box",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: isSmallScreen ? 1 : 2,
                    flexDirection: "row",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: "Roboto",
                      fontSize: "14px",
                      lineHeight: "100%",
                      color: "#48484aaf",
                    }}
                  >
                    {t("Click to select color")}
                  </Typography>
                  <Box
                    sx={{
                      width: 52,
                      height: 32,
                      borderRadius: "4px",
                      backgroundColor: color || "#ADD8E6",
                      cursor: "pointer",
                      border: "2px solid #95A0B3",
                    }}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={3} mb={1.5}>
                <StandardLabel label={t("Air Condition")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.airCondition && errors.airCondition)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.airCondition || ""}
                    onChange={(e) => handleDropdownChange(e, "airCondition")}
                    onBlur={() => handleBlur("airCondition")}
                    inputProps={{ "aria-label": "Air Condition" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Air Condition")}
                          </Typography>
                        );
                      }
                      return getOptionLabel(AIR_CONDITION_OPTIONS, selected);
                    }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {AIR_CONDITION_OPTIONS.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.airCondition && errors.airCondition && (
                    <FormHelperText error>{errors.airCondition}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Operations Section */}
        <Paper
          sx={{
            height: 52,
            width: "100%",
            marginRight: "20px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            marginTop: 4,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontFamily: "Roboto",
              fontSize: isSmallScreen ? "20px" : "24px",
              lineHeight: "100%",
              color: "#FFFFFF",
              marginBottom: isExtraSmallScreen ? "10px" : 0,
            }}
          >
            {t("Operations")}
          </Typography>
        </Paper>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {hasPermission("/api/site/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("site")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.sites && errors.sites)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.sites || ""}
                    onChange={(e) => handleDropdownChange(e, "site")}
                    onBlur={() => handleBlur("sites")}
                    inputProps={{ "aria-label": "Site" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Site")}
                          </Typography>
                        );
                      }
                      const selectedSite = sites.find(
                        (site) => String(site.id) === String(selected),
                      );
                      return selectedSite
                        ? selectedSite.name || selectedSite.siteId
                        : "";
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    sx={selectStyles}
                    IconComponent={ArrowDownIcon}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {sites.map((site) => (
                      <MenuItem key={site.id} value={site.id}>
                        {site.name || site.siteId}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.sites && errors.sites && (
                    <FormHelperText error>{errors.sites}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {hasPermission("/api/route/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("route")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.routes && errors.routes)}
                >
                  <Select
                    displayEmpty
                    value={
                      routes.some(
                        (route) => String(route.id) === String(vehicleFormData.routes),
                      )
                        ? vehicleFormData.routes
                        : ""
                    }
                    disabled={!vehicleFormData.sites}
                    onChange={(e) => handleDropdownChange(e, "route")}
                    onBlur={() => handleBlur("routes")}
                    inputProps={{ "aria-label": "Route" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {vehicleFormData.sites
                              ? t("Select Route")
                              : t("Select a site first")}
                          </Typography>
                        );
                      }
                      const selectedRoute = routes.find(
                        (route) => String(route.id) === String(selected),
                      );
                      return selectedRoute ? selectedRoute.name : "";
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    sx={selectStyles}
                    IconComponent={ArrowDownIcon}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {routes.map((route) => (
                      <MenuItem key={route.id} value={route.id}>
                        {route.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.routes && errors.routes && (
                    <FormHelperText error>{errors.routes}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {hasPermission("/api/driver/organization", "GET") && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("driver")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.driverId && errors.driverId)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.driverId || ""}
                    onChange={(e) => handleDropdownChange(e, "driver")}
                    onBlur={() => handleBlur("driverId")}
                    inputProps={{ "aria-label": "Driver" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Driver")}
                          </Typography>
                        );
                      }
                      const selectedDriver = drivers.find(
                        (driver) => String(driver.id) === String(selected),
                      );
                      return selectedDriver
                        ? `${selectedDriver.first_name} ${selectedDriver.last_name}`
                        : "";
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    sx={selectStyles}
                    IconComponent={ArrowDownIcon}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {drivers.map((driver) => (
                      <MenuItem key={driver.id} value={driver.id}>
                        {driver.first_name} {driver.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.driverId && errors.driverId && (
                    <FormHelperText error>{errors.driverId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            {(hasPermission("/api/employee/organization", "GET") ||
              hasPermission("/api/employee", "GET")) && (
              <Grid item xs={12} md={userFeatures.length > 0 ? 4 : 6} mb={1}>
                <StandardLabel label={t("Attendant")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.attendantId && errors.attendantId)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.attendantId || ""}
                    onChange={(e) => handleDropdownChange(e, "attendant")}
                    onBlur={() => handleBlur("attendantId")}
                    inputProps={{ "aria-label": "Attendant" }}
                    renderValue={(selected) => {
                      if (!selected) {
                        return (
                          <Typography color="text.secondary">
                            {t("Select Attendant")}
                          </Typography>
                        );
                      }
                      const selectedAttendant = attendants.find(
                        (attendant) => String(attendant.id) === String(selected),
                      );
                      if (!selectedAttendant) return "";
                      return `${selectedAttendant.firstName || selectedAttendant.first_name || ""} ${selectedAttendant.lastName || selectedAttendant.last_name || ""}`.trim();
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 200,
                          overflowY: "auto",
                        },
                      },
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                    sx={selectStyles}
                    IconComponent={ArrowDownIcon}
                  >
                    <MenuItem value="">
                      <em>{t("None")}</em>
                    </MenuItem>
                    {attendants.map((attendant) => (
                      <MenuItem key={attendant.id} value={attendant.id}>
                        {attendant.firstName || attendant.first_name}{" "}
                        {attendant.lastName || attendant.last_name}
                      </MenuItem>
                    ))}
                  </Select>
                  {touched.attendantId && errors.attendantId && (
                    <FormHelperText error>{errors.attendantId}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {isRouteEyeAdmin() && (
              <Grid item xs={12} sm={6} mb={1.5}>
                <StandardLabel label={t("Device ID")} />
                <FormControl
                  fullWidth
                  variant="outlined"
                  error={!!(touched.device_id && errors.device_id)}
                >
                  <Select
                    displayEmpty
                    value={vehicleFormData.device_id || ""}
                    onChange={(e) =>
                      handleInputChange(
                        { target: { value: e.target.value } },
                        "device_id",
                      )
                    }
                    onBlur={() => handleBlur("device_id")}
                    inputProps={{ "aria-label": "Device ID" }}
                    sx={selectStyles}
                  >
                    <MenuItem value="">
                      <em>{t("Select Device ID")}</em>
                    </MenuItem>
                    {Array.isArray(deviceData) &&
                      deviceData
                        .filter((device) => device && device.imeiNumber)
                        .map((device) => (
                          <MenuItem key={device.id} value={device.imeiNumber}>
                            {device.imeiNumber
                              ? `${device.deviceId} (${device.imeiNumber})`
                              : device.deviceId}
                          </MenuItem>
                        ))}
                  </Select>
                  {touched.device_id && errors.device_id && (
                    <FormHelperText error>{errors.device_id}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12} md={isRouteEyeAdmin() ? 6 : 12} mb={1.5}>
              <StandardLabel label={t("description")} />
              <TextField
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                placeholder={t("Type Description")}
                value={vehicleFormData.description || ""}
                onChange={(e) => handleInputChange(e, "description")}
                onBlur={() => handleBlur("description")}
                error={!!(touched.description && errors.description)}
                helperText={
                  touched.description && errors.description
                    ? errors.description
                    : ""
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    ...inputStyles["& .MuiOutlinedInput-root"],
                    height: "auto",
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Maintenance Section */}
        <Paper
          sx={{
            height: 52,
            width: "100%",
            marginRight: "20px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            marginTop: 4,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontFamily: "Roboto",
              fontSize: isSmallScreen ? "20px" : "24px",
              lineHeight: "100%",
              color: "#FFFFFF",
              marginBottom: isExtraSmallScreen ? "10px" : 0,
            }}
          >
            {t("Maintenance")}
          </Typography>
        </Paper>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3} mb={1}>
              <StandardLabel label={t("Last Service Date")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ max: todayForDateInput }}
                value={
                  formatDateForInput(vehicleFormData.lastServiceDate) || ""
                }
                onChange={(e) => handleInputChange(e, "lastServiceDate")}
                onBlur={() => handleBlur("lastServiceDate")}
                error={!!(touched.lastServiceDate && errors.lastServiceDate)}
                helperText={
                  touched.lastServiceDate && errors.lastServiceDate
                    ? errors.lastServiceDate
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={3} mb={1}>
              <StandardLabel label={t("Service Due Date")} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={
                    vehicleFormData.serviceDueDate
                      ? new Date(vehicleFormData.serviceDueDate)
                      : null
                  }
                  onChange={(newValue) => {
                    handleInputChange(
                      { target: { value: newValue } },
                      "serviceDueDate",
                    );
                  }}
                  format={getDateInputFormat()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!(
                        touched.serviceDueDate && errors.serviceDueDate
                      ),
                      placeholder: getDatePlaceholder(),
                      helperText:
                        touched.serviceDueDate && errors.serviceDueDate
                          ? errors.serviceDueDate
                          : "",
                      sx: {
                        ...inputStyles,
                        "& .MuiOutlinedInput-root": {
                          ...inputStyles["& .MuiOutlinedInput-root"],
                          "& input": {
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "16px",
                            color: "#111214",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3} mb={1}>
              <StandardLabel label={t("Mechanic")} />
              <TextField
                fullWidth
                variant="outlined"
                placeholder={`${t("Type Mechanic")} (e.g. Anil Das)`}
                value={vehicleFormData.mechanic || ""}
                onChange={(e) => handleInputChange(e, "mechanic")}
                onBlur={() => handleBlur("mechanic")}
                error={!!(touched.mechanic && errors.mechanic)}
                helperText={
                  touched.mechanic && errors.mechanic ? errors.mechanic : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={3} mb={1}>
              <StandardLabel label={t("Mechanic Phone Number")} />
              <TextField
                fullWidth
                variant="outlined"
                placeholder={t("e.g., +91 12304 56789")}
                value={vehicleFormData.mechanicPhoneNumber || ""}
                onChange={(e) => handleInputChange(e, "mechanicPhoneNumber")}
                onBlur={() => handleBlur("mechanicPhoneNumber")}
                error={
                  !!(touched.mechanicPhoneNumber && errors.mechanicPhoneNumber)
                }
                helperText={
                  touched.mechanicPhoneNumber && errors.mechanicPhoneNumber
                    ? errors.mechanicPhoneNumber
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Compliance Section */}
        <Paper
          sx={{
            height: 52,
            width: "100%",
            marginRight: "20px",
            borderRadius: "8px",
            padding: "12px 24px",
            backgroundColor: "#647490",
            boxShadow: "-2px 2px 5px 0px #00000040",
            display: "flex",
            marginTop: 4,
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: 500,
              fontFamily: "Roboto",
              fontSize: isSmallScreen ? "20px" : "24px",
              lineHeight: "100%",
              color: "#FFFFFF",
              marginBottom: isExtraSmallScreen ? "10px" : 0,
            }}
          >
            {t("Compliance")}
          </Typography>
        </Paper>
        <Box sx={{ mt: 4 }}>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Insurance Provider")} />
              <TextField
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                placeholder={t("Type Insurance Provider")}
                value={vehicleFormData.insuranceProvider || ""}
                onChange={(e) => handleInputChange(e, "insuranceProvider")}
                onBlur={() => handleBlur("insuranceProvider")}
                error={
                  !!(touched.insuranceProvider && errors.insuranceProvider)
                }
                helperText={
                  touched.insuranceProvider && errors.insuranceProvider
                    ? errors.insuranceProvider
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Insurance Policy Number")} />
              <TextField
                fullWidth
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                placeholder={`${t("Type Policy Number")} (e.g.ABC1234567)`}
                value={vehicleFormData.insurancePolicyNumber || ""}
                onChange={(e) => handleInputChange(e, "insurancePolicyNumber")}
                onBlur={() => handleBlur("insurancePolicyNumber")}
                error={
                  !!(
                    touched.insurancePolicyNumber &&
                    errors.insurancePolicyNumber
                  )
                }
                helperText={
                  touched.insurancePolicyNumber && errors.insurancePolicyNumber
                    ? errors.insurancePolicyNumber
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Insurance Policy Expiry Date")} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={
                    vehicleFormData.insurancePolicyExpiry
                      ? new Date(vehicleFormData.insurancePolicyExpiry)
                      : null
                  }
                  onChange={(newValue) => {
                    handleInputChange(
                      { target: { value: newValue } },
                      "insurancePolicyExpiry",
                    );
                  }}
                  format={getDateInputFormat()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!(
                        touched.insurancePolicyExpiry &&
                        errors.insurancePolicyExpiry
                      ),
                      placeholder: getDatePlaceholder(),
                      helperText:
                        touched.insurancePolicyExpiry &&
                        errors.insurancePolicyExpiry
                          ? errors.insurancePolicyExpiry
                          : "",
                      sx: {
                        ...inputStyles,
                        "& .MuiOutlinedInput-root": {
                          ...inputStyles["& .MuiOutlinedInput-root"],
                          "& input": {
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "16px",
                            color: "#111214",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Re-Registration Due Date")} />
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  value={
                    vehicleFormData.reRegistration
                      ? new Date(vehicleFormData.reRegistration)
                      : null
                  }
                  onChange={(newValue) => {
                    setVehicleFormData((prev) => ({
                      ...prev,
                      reRegistration: newValue,
                      reregistrationDueDate: newValue,
                    }));
                    setTouched((prev) => ({
                      ...prev,
                      reregistrationDueDate: true,
                    }));
                  }}
                  format={getDateInputFormat()}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!(
                        touched.reregistrationDueDate &&
                        errors.reregistrationDueDate
                      ),
                      placeholder: getDatePlaceholder(),
                      helperText:
                        touched.reregistrationDueDate &&
                        errors.reregistrationDueDate
                          ? errors.reregistrationDueDate
                          : "",
                      sx: {
                        ...inputStyles,
                        "& .MuiOutlinedInput-root": {
                          ...inputStyles["& .MuiOutlinedInput-root"],
                          "& input": {
                            fontFamily: "Roboto",
                            fontWeight: 400,
                            fontSize: "16px",
                            color: "#111214",
                          },
                        },
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Last Emission Test Date")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                inputProps={{ max: todayForDateInput }}
                value={
                  formatDateForInput(vehicleFormData.lastEmissionTestDate) || ""
                }
                onChange={(e) => handleInputChange(e, "lastEmissionTestDate")}
                onBlur={() => handleBlur("lastEmissionTestDate")}
                error={
                  !!(
                    touched.lastEmissionTestDate && errors.lastEmissionTestDate
                  )
                }
                helperText={
                  touched.lastEmissionTestDate && errors.lastEmissionTestDate
                    ? errors.lastEmissionTestDate
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
            <Grid item xs={12} md={4} mb={1}>
              <StandardLabel label={t("Emission Test Due Date")} />
              <TextField
                fullWidth
                type="date"
                variant="outlined"
                InputLabelProps={{ shrink: false }}
                value={
                  formatDateForInput(vehicleFormData.emissionTestDueDate) || ""
                }
                onChange={(e) => handleInputChange(e, "emissionTestDueDate")}
                onBlur={() => handleBlur("emissionTestDueDate")}
                error={
                  !!(touched.emissionTestDueDate && errors.emissionTestDueDate)
                }
                helperText={
                  touched.emissionTestDueDate && errors.emissionTestDueDate
                    ? errors.emissionTestDueDate
                    : ""
                }
                sx={inputStyles}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleColorPickerClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <ChromePicker color={color} onChange={(color) => setColor(color.hex)} />
      </Popover>
    </Box>
  );
}

export default VehicleAddDetails;
